import { UserDto, UserRole, UserStatus } from "@/common/api/datasources/Api";
import { useUsersQuery } from "@/common/api/services/users";
import { useAuth } from "@/common/hooks/useAuth";
import { useAuthQuery } from "@/common/api/services/auth";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useState, ReactNode } from "react";

interface DeleteUserDialogProps {
    user: UserDto;
    trigger: ReactNode;
    onSuccess?: () => void;
    isSelfDelete?: boolean;
}

export const DeleteUserDialog = ({ user, trigger, onSuccess, isSelfDelete = false }: DeleteUserDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [hardDelete, setHardDelete] = useState(false);

    // Отримання аутентифікації та даних користувача
    const { profileQuery } = useAuthQuery();
    const { data: currentUser } = useQuery(profileQuery);
    const isAdmin = currentUser?.role === UserRole.ADMIN;

    const { deleteUserMutation, changeUserStatusMutation } = useUsersQuery();

    // Мутація для зміни статусу користувача на INACTIVE замість м'якого видалення
    const { mutate: setUserInactive } = useMutation({
        mutationFn: () => changeUserStatusMutation.mutationFn({
            userUUID: user.userUUID,
            status: UserStatus.INACTIVE
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["usersStats"] });

            toast({
                title: t("users.toast.statusChangeSuccess.title"),
                description: t("users.toast.inactiveSuccess.description"),
            });

            if (onSuccess) {
                onSuccess();
            }
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("users.toast.statusChangeError.title"),
                description: t("users.toast.statusChangeError.description"),
            });
        },
    });

    // Мутація для видалення
    const { mutate: deleteUser } = useMutation({
        mutationFn: () => deleteUserMutation.mutationFn(user.userUUID, hardDelete),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["usersStats"] });

            toast({
                title: t("users.toast.deleteSuccess.title"),
                description: hardDelete
                    ? t("users.toast.hardDeleteSuccess.description")
                    : t("users.toast.deleteSuccess.description"),
            });

            if (onSuccess) {
                onSuccess();
            }
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("users.toast.deleteError.title"),
                description: t("users.toast.deleteError.description"),
            });
        },
    });

    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                {trigger}
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Delete User</AlertDialogTitle>
                    <AlertDialogDescription>
                        This action will set the user status to Inactive. The user's data will be preserved but they won't be able to log in.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                {isAdmin && !isSelfDelete && (
                    <div className="flex items-center space-x-2 py-4">
                        <Switch
                            id="hard-delete"
                            checked={hardDelete}
                            onCheckedChange={setHardDelete}
                        />
                        <Label htmlFor="hard-delete" className="font-medium">
                            Hard delete
                        </Label>
                        {hardDelete && (
                            <p className="text-sm text-red-500 mt-1">
                                {t("users.delete.hardDeleteWarning")}
                            </p>
                        )}
                    </div>
                )}
                <AlertDialogFooter>
                    <AlertDialogCancel>
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={() => hardDelete ? deleteUser() : setUserInactive()}
                        className="bg-red-600 hover:bg-red-700"
                    >
                        {hardDelete ? "Delete Permanently" : "Set Inactive"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}; 